import { useMemo } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { usePrivacySettings } from '@/hooks/usePrivacySettings'
import { NAV_ITEMS, isNavVisible, type NavChild, type NavItem } from './navigation'

/**
 * The sidebar tree with everything the user cannot reach removed.
 *
 * <p>A module whose children are all hidden is dropped with them: opening a menu to find it
 * empty is a worse experience than the menu not being there, and it invites a support call
 * about a screen that was never missing.
 *
 * <p>Visibility only. Every route is guarded again by {@code RequirePermission} and every
 * endpoint by {@code @PreAuthorize}; hiding a link has never been the security boundary.
 */
export function useVisibleNav(): NavItem[] {
  const permissions = useAuthStore((state) => state.permissions)
  const isSuperAdmin = useAuthStore((state) => state.user?.roles.includes('SUPER_ADMIN') ?? false)
  const directoryOpen = usePrivacySettings().employeeDirectoryVisible

  return useMemo(() => {
    const context = {
      has: (permission: string) => isSuperAdmin || permissions.has(permission),
      directoryOpen,
    }

    return NAV_ITEMS.flatMap((module) => {
      if (!isNavVisible(module, context)) {
        return []
      }
      if (!module.children) {
        return [module]
      }
      const children: NavChild[] = module.children.filter((child) => isNavVisible(child, context))
      return children.length === 0 ? [] : [{ ...module, children }]
    })
  }, [permissions, isSuperAdmin, directoryOpen])
}
